import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';
import screen2 from '../screen2.jpg';
import screen3 from '../screen3.jpg';
import screen4 from '../screen4.jpg';

const Chapitre8Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Afficher un écran de fin avec le score quand une collision est détectée
        </p>
      </InfoBox>

      <Separator type="text" text="Écran de fin" color="gray-darker" size="md" />
      <SectionTitle>💀 1/ Ajout de l'écran de fin</SectionTitle>

      <p>Lorsqu'une collision est détectée, la partie est finie et on affiche un écran de fin avec le score.</p>

      <p>Pour cela, on va créer une fonction <InlineCode>Gameover()</InlineCode> qui sera appelée si la fonction <InlineCode>VerifierCollision(t)</InlineCode> renvoie <InlineCode>true</InlineCode>.</p>

      <p>Dans la fonction <InlineCode>Gameover()</InlineCode>, on appelle la fonction p5.js <InlineCode>noLoop()</InlineCode> qui arrête le programme, notamment l'appel à la fonction <InlineCode>draw</InlineCode>.<br/>
      Ensuite, on redessine le fond et on affiche le score avec la fonction <InlineCode>text</InlineCode> :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>noLoop()</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Arrête d'appeler draw(), le jeu se fige</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <CodeBlock language="javascript">
{`noLoop(); // Arrête la boucle draw()
background(imageFond); 
fill(255); 
textSize(48);
text("💀 GAME OVER 💀", width / 2, height / 2 - 40);
textSize(32)
text("Score final : " + score, width / 2, height / 2 + 10);`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : Écran de fin">
        <p>Dans la condition <InlineCode>if (VerifierCollision(t) == true)</InlineCode>, appelle la fonction <InlineCode>Gameover()</InlineCode> :</p>

        <CodeBlock language="javascript">
{`// Vérifie les collisions
if (VerifierCollision(t) == true) {
  Gameover();
}`}
        </CodeBlock>

        <p>Tout en bas du fichier, crée une nouvelle fonction <InlineCode>Gameover</InlineCode> qui arrête le programme avec <InlineCode>noLoop()</InlineCode> et qui affiche le score :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- ECRAN DE FIN -------------------------------------
function Gameover() {
  noLoop();
  background(imageFond);
  fill(255);
  textSize(48);
  text("💀 GAME OVER 💀", width / 2, height / 2 - 40);
  textSize(32);
  text("Score final : " + score, width / 2, height / 2 + 10);
}`}
        </CodeBlock>

        <br/>
        <Result>Lors d'une collision, le jeu s'arrête et un écran de fin apparaît avec le score.</Result>
      </Note>

      <Separator type="text" text="Problème de l'oiseau" color="gray-darker" size="md" />
      <SectionTitle>🐦 2/ Pourquoi voit-on encore l'oiseau ?</SectionTitle>

      <p>Sur l'écran de fin, l'oiseau est encore dessiné alors que <InlineCode>noLoop()</InlineCode> devrait tout arrêter, non ?</p>

      <p>En réalité, <InlineCode>noLoop()</InlineCode> empêche <InlineCode>draw</InlineCode> d'être appelé à nouveau, mais n'empêche pas le <InlineCode>draw</InlineCode> en cours de se terminer !<br/>
      Or, une fois la collision détectée, il reste du code en dessous, notamment l'affichage de l'oiseau.</p>

      <p>Il faut donc appeler la fonction <InlineCode>return;</InlineCode> pour sortir du <InlineCode>draw</InlineCode> immédiatement.</p>

      <Note type="tip" title="💡 Comment fonctionne return ?">
        <p><InlineCode>return;</InlineCode> permet de sortir immédiatement d'une fonction, sans exécuter le code qui suit.</p>
        <p>Cela nous permet d'arrêter <InlineCode>draw()</InlineCode> dès qu'une collision est détectée, pour éviter que l'oiseau soit redessiné.</p>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : return après l'écran de fin">
        <p>Juste après avoir appelé <InlineCode>Gameover()</InlineCode> dans <InlineCode>draw</InlineCode>, ajoute un <InlineCode>return;</InlineCode> :</p>

        <CodeBlock language="javascript">
{`// Vérifie les collisions
if (VerifierCollision(t) == true) {
  Gameover();
  return;
}`}
        </CodeBlock>

        <br/>
        <Result>Écran de fin propre, l'oiseau n'est plus dessiné.</Result>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 3/ Code complet : Écran de fin</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;
let imageOiseau;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes
let oiseauGravite = 800; // Gravité à laquelle l'oiseau tombe
let oiseauImpulsion = -300; // Force d'impulsion quand on clique

let tuyauTemps = 0;
let tuyaux = [];
let score = 0;

// Oiseau
let oiseau = {
  x: 150,
  y: 250,
  vitesseY: 0
};

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
  imageOiseau = loadImage("flappy1.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);

    // Supprime s'il est complètement à gauche
    if (t.x + imageTuyauHaut.width < 0) {
      tuyaux.splice(i, 1);
    }

    // Vérifie si l’oiseau vient de passer le tuyau
    if (t.franchi == false &&  oiseau.x > t.x) {
      score = score + 1;
      t.franchi = true;
    }

    // Vérifie les collisions
    if (VerifierCollision(t) == true) {
      Gameover();
      return;
    }
  }

  // Créer un nouveau tuyau toutes les x secondes
  tuyauTemps = tuyauTemps - dt;
  if (tuyauTemps <= 0) {
    tuyauTemps = tuyauIntervalle;
    let positionX = width;
    let positionY = random(100, 400);
    tuyaux.push({ x: positionX, y: positionY, franchi: false });
  }

  // Déplacement de l'oiseau
  oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;
  oiseau.y = oiseau.y + oiseau.vitesseY * dt;
  image(imageOiseau, oiseau.x, oiseau.y);

  // Affichage du score
  textAlign(CENTER, TOP);
  textSize(64);
  textStyle(BOLD);
  fill(255);
  text(score, width / 2, 20);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  oiseau.vitesseY = oiseauImpulsion;
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}

//--------------------------------- COLLISION ------------------------------------------
function VerifierCollision(t) {
  // Collision avec le tuyau du haut
  if (
    oiseau.x + imageOiseau.width > t.x &&
    oiseau.x < t.x + imageTuyauHaut.width &&
    oiseau.y < t.y
  ) {
    return true;
  }

  // Collision avec le tuyau du bas
  if (
    oiseau.x + imageOiseau.width > t.x &&
    oiseau.x < t.x + imageTuyauBas.width &&
    oiseau.y + imageOiseau.height > t.y + tuyauEspace
  ) {
    return true;
  }

  // Collision avec le haut de l'écran
  if (oiseau.y < 0) {
    return true;
  }

  // Collision avec le bas de l'écran
  if (oiseau.y + imageOiseau.height > height) {
    return true;
  }

  // Pas de collision
  return false;
}

//--------------------------------- ECRAN DE FIN -------------------------------------
function Gameover() {
  noLoop();
  background(imageFond);
  fill(255);
  textSize(48);
  text("💀 GAME OVER 💀", width / 2, height / 2 - 40);
  textSize(32);
  text("Score final : " + score, width / 2, height / 2 + 10);
}
`}
      </CodeBlock>

      <p>🎯 Dans le prochain chapitre, on s'occupe du son et de l'équilibrage du jeu !</p>
    </>
  );
};

export default Chapitre8Content; 